import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
import { useAuth } from "./AuthProvider";
import { setFrontendMode } from "@/shared/api/auth";
import { useUserStore } from "@/entities/user/model";
const GenerationModeContext = createContext(undefined);
/** Кэш последнего ответа бэка — для гостей, у которых нет GET /users/me. */
const CACHE_KEY = "frontend_mode_cache";
/** Старый ключ локального переключателя — больше не источник истины. */
const LEGACY_STORAGE_KEY = "songGenerationMode";
function isMode(v) {
    return v === "classic" || v === "chat";
}
function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (isMode(parsed.assigned) && isMode(parsed.current)) {
            return { assigned: parsed.assigned, current: parsed.current };
        }
    }
    catch {
        /* ignore */
    }
    return null;
}
function writeCache(mode) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(mode));
        localStorage.setItem(LEGACY_STORAGE_KEY, mode.current);
    }
    catch {
        /* ignore */
    }
}
function readLegacyFallback() {
    try {
        const saved = localStorage.getItem(LEGACY_STORAGE_KEY);
        if (isMode(saved))
            return saved;
    }
    catch {
        /* ignore */
    }
    return "classic";
}
/**
 * Гость не ходит в GET /users/me (403). Чтобы узнать assigned без затирания
 * уже выбранного current, сначала смотрим кэш; если кэша нет (первый визит) —
 * probe POST + restore к assigned.
 */
async function resolveGuestMode() {
    const cached = readCache();
    if (cached) {
        return cached;
    }
    const probe = await setFrontendMode("classic");
    if (probe.assigned !== probe.current) {
        const restored = await setFrontendMode(probe.assigned);
        writeCache(restored);
        return restored;
    }
    writeCache(probe);
    return probe;
}
export const GenerationModeProvider = ({ children, }) => {
    const { user, isAuthenticated, isGuest, isLoading: authLoading } = useAuth();
    const cached = readCache();
    const [generationMode, setGenerationModeState] = useState(() => cached?.current ?? readLegacyFallback());
    const [isModeReady, setIsModeReady] = useState(Boolean(cached));
    const syncGenRef = useRef(0);
    const applyMode = useCallback((mode) => {
        writeCache(mode);
        setGenerationModeState(mode.current);
        setIsModeReady(true);
    }, []);
    // Синхронизация с бэком после готовности auth.
    useEffect(() => {
        if (authLoading)
            return;
        const gen = ++syncGenRef.current;
        let cancelled = false;
        const sync = async () => {
            try {
                if (isAuthenticated && user?.frontend_mode) {
                    if (cancelled || gen !== syncGenRef.current)
                        return;
                    applyMode(user.frontend_mode);
                    return;
                }
                if (isAuthenticated && user && !user.frontend_mode) {
                    // Вне scope сплита (бот и т.п.) — оставляем локальный фолбэк.
                    if (cancelled || gen !== syncGenRef.current)
                        return;
                    setIsModeReady(true);
                    return;
                }
                if (isGuest) {
                    const mode = await resolveGuestMode();
                    if (cancelled || gen !== syncGenRef.current)
                        return;
                    applyMode(mode);
                    return;
                }
                // Нет сессии — ждём гостя/логин; не блокируем UI навсегда.
                if (cancelled || gen !== syncGenRef.current)
                    return;
                setIsModeReady(true);
            }
            catch (e) {
                console.error("frontend_mode sync failed:", e);
                if (!cancelled && gen === syncGenRef.current) {
                    setIsModeReady(true);
                }
            }
        };
        void sync();
        return () => {
            cancelled = true;
        };
    }, [
        authLoading,
        isAuthenticated,
        isGuest,
        user?.id,
        user?.frontend_mode?.assigned,
        user?.frontend_mode?.current,
        applyMode,
    ]);
    const setGenerationMode = useCallback((mode) => {
        if (mode === generationMode)
            return;
        // Оптимистично обновляем UI.
        setGenerationModeState(mode);
        void setFrontendMode(mode)
            .then((res) => {
            applyMode(res);
            useUserStore.getState().updateUser({ frontend_mode: res });
        })
            .catch((e) => {
            console.error("frontend_mode switch failed:", e);
            // Откат к кэшу/прежнему, если бэк отклонил.
            const cachedMode = readCache();
            if (cachedMode) {
                setGenerationModeState(cachedMode.current);
            }
        });
    }, [generationMode, applyMode]);
    const toggleGenerationMode = useCallback(() => {
        setGenerationMode(generationMode === "classic" ? "chat" : "classic");
    }, [generationMode, setGenerationMode]);
    return (_jsx(GenerationModeContext.Provider, { value: {
            generationMode,
            setGenerationMode,
            toggleGenerationMode,
            isModeReady,
        }, children: children }));
};
export const useGenerationMode = () => {
    const context = useContext(GenerationModeContext);
    if (context === undefined) {
        throw new Error("useGenerationMode must be used within a GenerationModeProvider");
    }
    return context;
};
